import React, { useState, useEffect } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../firebase";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../auth";
import { recordFirstAdminLogin } from "../../api/users";

function getLoginErrorMessage(error) {
  switch (error?.code) {
    case "auth/invalid-credential":
    case "auth/invalid-email":
    case "auth/user-not-found":
    case "auth/wrong-password":
      return "We couldn't sign you in. Check your email and password and try again.";
    case "auth/user-disabled":
      return "This account has been disabled. Please contact support for help.";
    case "auth/too-many-requests":
      return "Too many unsuccessful attempts. Please wait a few minutes or reset your password.";
    case "auth/network-request-failed":
      return "We couldn't reach the sign-in service. Check your connection and try again.";
    default:
      return "We couldn't sign you in right now. Please try again.";
  }
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const navigate = useNavigate();
  const { user, subscription, loading, subscriptionLoading } = useAuth();

  useEffect(() => {
    if (loading || subscriptionLoading) return;
    if (user && subscription?.account?.id) {
      navigate("/posts/attraction", { replace: true });
    }
  }, [loading, subscriptionLoading, user, subscription, navigate]);
  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    setMsg(null);
    setBusy(true);

    try {
      const cred = await signInWithEmailAndPassword(auth, email, pw);
      await cred.user.getIdTokenResult(true);
      try {
        await recordFirstAdminLogin();
      } catch (eventError) {
        console.warn("Unable to record admin-first-login event", eventError);
      }
      navigate("/posts/attraction", { replace: true });
    } catch (e) {
      setErr(getLoginErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-sm-10 col-md-6 col-lg-4">
          <div className="text-center mb-3">
            <h2 className="fw-bold mb-1">Login</h2>
            <div className="text-muted small">BounceHouse Admin</div>
          </div>

          <div className="card shadow-sm">
            <div className="card-body p-4">
              <form onSubmit={onSubmit} autoComplete="off">
                <div className="mb-3 text-start">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    placeholder=""
                    autoComplete="off"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3 text-start">
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    name="password"
                    className="form-control"
                    placeholder=""
                    autoComplete="new-password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    required
                  />
                </div>

                <button
                  disabled={busy}
                  type="submit"
                  className="btn btn-primary w-100"
                >
                  {busy ? "Signing in…" : "Sign in"}
                </button>
              </form>

              <div className="d-flex justify-content-between mt-3">
                <Link to="/forgotpassword" className="small text-decoration-none">
                  Forgot your password?
                </Link>
              </div>

              {msg && <div className="alert alert-success mt-3 mb-0 py-2">{msg}</div>}
              {err && (
                <div role="alert" className="alert alert-danger mt-3 mb-0 py-2">
                  {err}
                  {err.includes("email us") ? (
                    <div className="mt-2"><a href="mailto:help@bouncehouse.com">help@bouncehouse.com</a></div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          <div className="text-center mt-3">
            <Link to="/pricing" className="small text-decoration-none">
              View plans
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
